import { type IMultiTaskingService } from '../../multi-tasking';
import { buildQueryString } from 'components/utils/navigation/navigation';

export interface INavigationService {
    //  subscribeOnScope($scope: any, cb: Function): void;
    subscribeOnSearchVariable($scope: any, variable: string, cb: Function): void;
    subscribeOnTab($scope: any, cb: (string) => boolean): void;
    subscribeOnSubTab($scope: any, cb: (string) => boolean): void;
    subscribeOnFilters($scope: any, cb: (INavigationFilters) => boolean): void;
    subscribeOnCommand($scope: any, cb: (INavigationCommand) => boolean): void;
    back(): Promise<void>;
    isOpenedFromAnotherModule(): boolean;
}

export interface INavigationFilters {
    [key: string]: any;
}

export interface INavigationRequestParams {
    tab?: string;
    subTab?: string;
    filters?: string;
    command?: string;
}

export interface INavigationCommand {
    name: string;
    params?: { [key: string]: any };
}

export interface INavigationOptions {
    tab?: string;
    subTab?: string;
    filters?: INavigationFilters;
    command?: INavigationCommand;
}

export abstract class NavigationService implements INavigationService {
    protected multiTasking: IMultiTaskingService;
    protected $location: angular.ILocationService;
    protected $timeout: angular.ITimeoutService;

    protected constructor(protected $injector: angular.auto.IInjectorService) {
        this.$location = this.$injector.get('$location');
        this.$timeout = this.$injector.get('$timeout');
        this.multiTasking = this.$injector.get('multiTasking') as IMultiTaskingService;
    }

    protected buildRequestParamsStr(options: INavigationOptions) {
        return buildQueryString({ ...this.buildRequestParams(options) });
    }

    protected buildRequestParams(options: INavigationOptions): INavigationRequestParams {
        const params: INavigationRequestParams = {};

        if (options.tab) {
            params.tab = options.tab;
        }

        if (options.subTab) {
            params.subTab = options.subTab;
        }

        if (options.filters) {
            params.filters = JSON.stringify(options.filters);
        }

        if (options.command) {
            params.command = JSON.stringify(options.command);
        }

        return params;
    }

    protected subscribeOnScope($scope, cb: Function): void {
        cb();
        const unSubscribe = $scope.$on('$locationChangeSuccess', () => {
            this.$timeout(() => {
                if (!$scope.$$destroyed) {
                    cb();
                }
            }, 1);
        });

        $scope.$on('$destroy', () => {
            unSubscribe();
        });
    }

    public subscribeOnSearchVariable($scope, variable: string, cb: Function): void {
        this.subscribeOnScope($scope, () => {
            this.onSearchVariable(variable, cb);
        });
    }

    public onSearchVariable(variable: string, cb: Function) {
        const search = this.$location.search(),
            value = search[variable];

        if (value !== undefined && cb(value)) {
            this.$location.search(variable, null);
        }
    }

    public subscribeOnTab($scope, cb: (string) => boolean): void {
        this.subscribeOnSearchVariable($scope, 'tab', cb);
    }

    public subscribeOnSubTab($scope, cb: (string) => boolean): void {
        this.subscribeOnSearchVariable($scope, 'subTab', cb);
    }

    public subscribeOnFilters($scope, cb: (INavigationFilters) => boolean): void {
        this.subscribeOnSearchVariable($scope, 'filters', filtersStr => {
            return cb(JSON.parse(filtersStr));
        });
    }

    public subscribeOnCommand($scope, cb: (INavigationCommand) => boolean): void {
        this.subscribeOnSearchVariable($scope, 'command', filtersStr => {
            return cb(JSON.parse(filtersStr));
        });
    }

    public back(): Promise<void> {
        return this.multiTasking.back();
    }

    public isOpenedFromAnotherModule() {
        const module = this.multiTasking.getCurrentModule();
        const link = this.multiTasking.getBackLink();
        return !this.multiTasking.isGroupModule(module) && link != '/';
    }

    protected openPageByUrl(url: string) {
        this.$location.url(url);
        return this.$timeout();
    }

    protected openSinglePage(url?: string, options?: INavigationOptions): Promise<void> {
        const path = url[0] === '/' ? url : '/' + url,
            page = this.multiTasking.searchPageByLink(path),
            current = this.multiTasking.getCurrentPage(),
            searchParams = options && this.buildRequestParams(options);

        const openLocation = () => {
            this.$location.path(url);
            if (searchParams) {
                this.$location.search(searchParams);
            }
        };

        let promise = Promise.resolve();

        // opened another page
        if (!page && current.path) {
            return this.multiTasking.minimize().then(() => {
                return this.$timeout(() => {
                    openLocation();
                }, 300);
            });
        }

        // opened menu
        if (!page && !current.path) {
            openLocation();
            promise = <Promise<void>>this.$timeout();
        }

        if (page && !this.multiTasking.isCurrentPage(page)) {
            if (searchParams) {
                this.multiTasking.setSearchParams(searchParams);
            }
            this.multiTasking.openPage(page);
            promise = <Promise<void>>this.$timeout();
        }

        if (page && this.multiTasking.isCurrentPage(page)) {
            if (searchParams) {
                this.$location.search(searchParams);
            }
            this.multiTasking.maximize(page);
            promise = <Promise<void>>this.$timeout();
        }

        return promise;
    }
}
